import { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight } from "lucide-react";
import StepHeader from "@/components/StepHeader";
import { useAdvisorContent } from "@/hooks";
import { readAdvisorState, writeAdvisorState } from "@/data/polyFitData";

const YT = /(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/;

export function SceneMedia({ scene = {}, fallback = "", testId }) {
  const video = scene.video_url || "";
  const poster = scene.poster_url || fallback;
  const yt = video.match(YT);
  if (yt) return <iframe className="scene-media" title="Scenario video" src={`https://www.youtube.com/embed/${yt[1]}?autoplay=1&mute=1&loop=1&playlist=${yt[1]}&controls=0&playsinline=1&rel=0`} allow="autoplay; encrypted-media" allowFullScreen data-testid={`${testId}-youtube`} />;
  if (video) return <video className="scene-media" src={video} poster={poster} autoPlay muted loop playsInline data-testid={`${testId}-video`} />;
  return <img className="scene-media scene-poster" src={poster} alt="" data-testid={`${testId}-poster`} />;
}

export default function FitScenarioPage() {
  const { optionId } = useParams();
  const navigate = useNavigate();
  const { questions, products } = useAdvisorContent();
  const option = useMemo(() => questions.workplace?.options.find((o) => o.id === optionId), [questions, optionId]);
  const scene = option?.scene || {};

  useEffect(() => { window.scrollTo({ top: 0, behavior: "instant" }); }, [optionId]);

  if (!option) return null;
  const proceed = () => {
    const state = readAdvisorState();
    if (option.nextQuestionId) {
      writeAdvisorState({ ...state, currentQuestionId: option.nextQuestionId, recommendationId: "" });
      navigate(`/question/${option.nextQuestionId}`);
      return;
    }
    const product = products[option.resultProductId];
    writeAdvisorState({ ...state, recommendationId: option.resultProductId, completedSteps: option.completedSteps || 1, resultProductName: product?.brandName });
    navigate("/recommendation");
  };

  return (
    <main className="advisor-page scenario-page" data-testid="scenario-page">
      <StepHeader step={1} compact progressLabel="Your scenario" />
      <section className="scenario-stage reveal-in" data-testid="scenario-shell">
        <header className="scenario-head">
          <p className="step-kicker" data-testid="scenario-kicker">{option.heading || option.label}</p>
          <h1 data-testid="scenario-title">{scene.title}</h1>
          {scene.body && <p className="scenario-lede" data-testid="scenario-body">{scene.body}</p>}
        </header>
        <div className="scenario-frame" data-testid="scenario-media-frame"><SceneMedia scene={scene} fallback={option.imageUrl} testId="scenario-media" /></div>
        <div className="question-nav scenario-nav" data-testid="scenario-action-row">
          <button className="text-btn" type="button" onClick={() => navigate("/question/workplace")} data-testid="scenario-back-button"><ArrowLeft size={16} /> Back</button>
          <button className="primary-cta pill-cta" type="button" onClick={proceed} data-testid="scenario-continue-button">{scene.cta_label || "Move closer to find a solution"} <ArrowRight size={17} /></button>
        </div>
      </section>
    </main>
  );
}
